// TreeView.jsx — TERZO componente condiviso del design system (F2.3h).
// Albero gerarchico con gli STATI OBBLIGATORI del gestionale (regole UI 4,
// stessa filosofia di DataTable/CardGrid):
//   loading  → spinner + 'Caricamento…' (mai schermo che finge)
//   error    → messaggio + bottone 'Riprova' (callback onRetry del chiamante)
//   empty    → messaggio DEDICATO (emptyMessage), mai albero vuoto muto —
//              vale anche quando un FILTRO nasconde tutte le righe (parità
//              col v1 delle Categorie)
//   data     → righe appiattite in ordine depth-first; con righe visibili il
//              reload in corso/fallito NON svuota l'albero (precedenza in
//              tree-view-core.selectTreeState, che RIUSA selectTableState —
//              testata).
//
// API (pensata per riuso, mandato ondata 23):
//   roots          radici [{...,children:[]}] (forma libera: getChildren)
//   nodeKey        campo (stringa) o funzione node→chiave; fallback indice
//   getChildren?   (node) → figli (default node.children)
//   isVisible?     (node) → false = nodo e sottoalbero esclusi (filtro/search)
//   isExpanded?    (node) → true = figli emessi (lo stato è del CHIAMANTE:
//                  il componente non possiede espansioni — le viste hanno
//                  cicli di vita diversi, v. binding lessicali del monolite)
//   trailing?      (node) → valore truthy ⇒ riga di coda dopo i figli di un
//                  nodo aperto (es. "⋯ altre N categorie")
//   renderRow      RENDER-PROP (entry {node,depth,hasChildren,isOpen}) →
//                  la riga INTERA (chrome del consumatore: le Categorie usano
//                  le classi cat-* globali del monolite → parità pixel).
//                  Può ritornare un Fragment con più righe (es. prodotti).
//   renderTrailing? (entry {node,depth,data}) → resa della riga di coda
//   loading, error (stringa|Error), onRetry?, emptyMessage
//
// Stile sui token --shell-* con fallback campionati (pattern DataTable/
// CardGrid), stile iniettato UNA volta per documento. Primo consumatore
// reale: proc-v3 Categorie (CategoriesView.jsx).

import React from 'react';
import { selectTreeState, treeKeyOf, flattenTree } from './tree-view-core.js';

const STYLE_ID = 'shell-ui-treeview-style';

function css() {
  return [
    // stati: stessa resa del design system (box dedicato, mai silenzio)
    '.tv-state{background:var(--shell-surface,#fff);border:1px solid #e2e8f0;border-radius:12px;padding:24px;text-align:center;color:#94a3b8;font-size:13px}',
    '.tv-loading{display:flex;align-items:center;justify-content:center;gap:10px}',
    '.tv-spin{width:18px;height:18px;border-radius:50%;border:2px solid #cffafe;border-top-color:var(--shell-primary,#06b6d4);animation:tv-rot .8s linear infinite;display:inline-block}',
    '@keyframes tv-rot{to{transform:rotate(360deg)}}',
    '.tv-error{border-color:#fecaca;background:#fef2f2}',
    '.tv-errmsg{color:#b91c1c;font-size:13px;font-weight:600}',
    '.tv-retry{margin-top:12px;background:var(--shell-primary-strong,#0e7490);color:#fff;border:0;border-radius:8px;padding:8px 16px;font-weight:600;cursor:pointer;font-size:13px}',
  ].join('\n');
}

export function injectTreeViewStyle(doc) {
  if (!doc || doc.getElementById(STYLE_ID)) return;
  const st = doc.createElement('style');
  st.id = STYLE_ID;
  st.textContent = css();
  doc.head.appendChild(st);
}

export function TreeView(props) {
  const {
    roots, nodeKey, getChildren, isVisible, isExpanded, trailing,
    renderRow, renderTrailing, loading, error, onRetry, emptyMessage,
  } = props;
  const wrapRef = React.useRef(null);

  // stile una volta per documento, dal documento REALE in cui il componente vive
  React.useEffect(() => {
    const el = wrapRef.current;
    if (el && el.ownerDocument) injectTreeViewStyle(el.ownerDocument);
  }, []);

  // righe appiattite PRIMA della scelta dello stato: un filtro che nasconde
  // tutto = stato vuoto (v. tree-view-core)
  const entries = flattenTree(roots || [], { getChildren, isVisible, isExpanded, trailing });
  const state = selectTreeState({ loading, error, entries });

  let body;
  if (state === 'loading') {
    body = (
      <div className="tv-state tv-loading" role="status">
        <span className="tv-spin" />
        <span>Caricamento…</span>
      </div>
    );
  } else if (state === 'error') {
    const msg = error && error.message ? error.message : String(error);
    body = (
      <div className="tv-state tv-error" role="alert">
        <div className="tv-errmsg">{'⚠ ' + msg}</div>
        {typeof onRetry === 'function' && (
          <button type="button" className="tv-retry" onClick={() => onRetry()}>Riprova</button>
        )}
      </div>
    );
  } else if (state === 'empty') {
    body = <div className="tv-state tv-empty">{emptyMessage || 'Nessun elemento.'}</div>;
  } else {
    body = entries.map((entry, i) => {
      const baseKey = treeKeyOf(entry.node, nodeKey, i);
      if (entry.kind === 'trailing') {
        return (
          <React.Fragment key={'trailing-' + baseKey}>
            {typeof renderTrailing === 'function' ? renderTrailing(entry) : null}
          </React.Fragment>
        );
      }
      return (
        <React.Fragment key={baseKey}>
          {typeof renderRow === 'function' ? renderRow(entry) : null}
        </React.Fragment>
      );
    });
  }
  return <div className="tv-wrap" ref={wrapRef}>{body}</div>;
}
